import {
  CourseDepuisTarifFixeForm,
  TarifFixeCourseForm,
} from "../types/forms/tarif-fixe-course-form";
import { CreateCourseResult } from "../types/models/create-course-result";
import { TarifFixeCourse } from "../types/models/tarif-fixe-course";
import { HttpClient } from "./http-client";

/**
 * Itinéraires à tarif négocié.
 *
 * La configuration (create/update/activation, et la liste par entreprise) est
 * réservée au back-office ; `getMesTarifsFixes` est le pendant client, filtré
 * par le backend sur l'entreprise active du compte connecté.
 */
export class TarifFixeService {
  private readonly http: HttpClient;

  constructor(apiUrl: string) {
    this.http = new HttpClient(apiUrl);
  }

  /** Back-office : tous les tarifs d'un client, désactivés compris par défaut. */
  async getTarifsFixesByEntreprise(
    idEntreprise: number,
    actifsUniquement = false,
  ): Promise<TarifFixeCourse[]> {
    return this.http.get<TarifFixeCourse[]>(
      `/api/tarifs-fixes/entreprise/${idEntreprise}`,
      { query: { actifsUniquement } },
    );
  }

  /** Client : les tarifs actifs de son entreprise, ceux sur lesquels il peut réserver. */
  async getMesTarifsFixes(): Promise<TarifFixeCourse[]> {
    return this.http.get<TarifFixeCourse[]>("/api/tarifs-fixes/mes-tarifs-fixes");
  }

  async getTarifFixeById(id: number): Promise<TarifFixeCourse> {
    return this.http.get<TarifFixeCourse>(`/api/tarifs-fixes/${id}`);
  }

  /** Renvoie l'identifiant du tarif créé. */
  async createTarifFixe(tarifFixe: TarifFixeCourseForm): Promise<number> {
    return this.http.post<number>("/api/tarifs-fixes", tarifFixe);
  }

  async updateTarifFixe(
    id: number,
    tarifFixe: Omit<TarifFixeCourseForm, "idEntreprise">,
  ): Promise<void> {
    await this.http.put(`/api/tarifs-fixes/${id}`, tarifFixe);
  }

  /**
   * Active ou désactive un tarif. La désactivation tient lieu de suppression :
   * les courses déjà créées gardent la trace du tarif dont elles sont issues.
   */
  async setActivation(id: number, actif: boolean): Promise<void> {
    await this.http.patch(`/api/tarifs-fixes/${id}/activation`, { actif });
  }

  /**
   * Réserve une course sur un tarif fixe. Comme `createCourse`, la réponse
   * n'est pas la course mais de quoi enchaîner (id, et URL Stripe le cas
   * échéant).
   */
  async createCourseFromTarifFixe(
    idTarifFixe: number,
    course: CourseDepuisTarifFixeForm,
  ): Promise<CreateCourseResult> {
    return this.http.post<CreateCourseResult>(
      `/api/courses/depuis-tarif-fixe/${idTarifFixe}`,
      course,
    );
  }
}
